// Hero Section
export const heroData = {
  label: "Marketing · Systems · Content · Operations",
  headlineLine1: "You tell me what needs to happen.",
  headlineLine2: "I figure out how.",
  subtext: "I help founders and growing teams handle the overlap between content, systems, marketing, and the everyday work that keeps things moving when it starts getting messy.",
  locationLine: "Based in the Philippines · Working remotely worldwide",
  primaryCTA: {
    text: "See the work",
    url: "https://drive.google.com/drive/folders/1DKI0iZyt4sNhMMldwLg59v8pf0lUwO4i?usp=sharing"
  }
};

export const heroStats = [
  { value: "2.4M+", label: "social impressions generated" },
  { value: "170K+", label: "audience interactions" },
  { value: "110K+", label: "organic video views" },
  { value: "3,000+", label: "content assets produced" },
  { value: "5+ years", label: "supporting founders and teams" }
];

// Who I Help
export const whoIHelpData = {
  title: "Situations I usually step into",
  intro: "Good fit if any of this sounds familiar.",
  cards: [
    {
      title: "You have ideas. Nothing ships.",
      description: "The plan exists. Execution doesn't."
    },
    {
      title: "Your content is inconsistent at best.",
      description: "Sometimes you post. Sometimes you don't."
    },
    {
      title: "Your tools are a mess.",
      description: "Three apps, two sheets, zero clarity."
    },
    {
      title: "Launches fall apart at the last minute.",
      description: "Too many pieces, not enough owners."
    },
    {
      title: "You need someone who just handles it.",
      description: "No babysitting. No hand-holding. Just done."
    }
  ]
};

// Work Section (Bento)
export const workData = {
  title: "What I work on",
  intro: "The overlap between marketing, systems, content, and getting things out the door.",
  tiles: [
    {
      title: "Graphic Design",
      description: "Still images, animated posts, hero images, social visuals. Canva, Premiere, CapCut."
    },
    {
      title: "Video & Reels",
      description: "Short-form to long-form editing. Built for platform performance."
    },
    {
      title: "Writing & SEO",
      description: "Articles, blogs, newsletters, web copy, scripts. The kind that explains things clearly and actually gets read."
    },
    {
      title: "Content & Social",
      description: "Posts, reels, graphics, scheduling, reporting. Building and running social presence from scratch across TikTok, Instagram, Facebook, LinkedIn, YouTube."
    },
    {
      title: "Launch Support",
      description: "Setting up assets, organizing moving pieces, making sure things go live cleanly."
    },
    {
      title: "Systems & Tech",
      description: "Websites, CRMs, email flows, project boards, and custom-built tools. I learn whatever you're using or find something better."
    },
    {
      title: "Client & Founder Support",
      description: "Inbox, follow-ups, coordination, admin, candidate tracking. Keeps things calm."
    }
  ]
};

// Trust — case studies (flip cards with proofLink; no section buttons)
export const trustData = {
  title: "A few places I've actually done this",
  intro: "Real clients. Real work. No made-up case studies.",
  cards: [
    {
      client: "Murphy Associates Consulting",
      tags: "Website · Newsletters · Lead gen",
      description: "Built and launched the site on Squarespace, handled ongoing tech support, and built a custom lead magnet app on our own initiative.",
      proofLink: "https://can-i-afford-it-anna-murphy.vercel.app/"
    },
    {
      client: "Scot Nery",
      tags: "Content · Social · Ongoing VA",
      description: "Ran content and social for over a year. Grew to 1.74M impressions and 149K interactions across platforms.",
      proofLink: "https://drive.google.com/file/d/12ihkpWIWt7VzY_kz9JbowrzV4btLLFRj/view?usp=sharing"
    },
    {
      client: "Graphic Design Work",
      tags: "Across multiple clients",
      description: "Announcement posts, website hero images, social graphics for campaigns. Still and animated assets built for actual use.",
      proofLink: "https://drive.google.com/drive/folders/1iAj_v6aBZmR5xZfzSL9RY8Oxbgl-Qt3o?usp=drive_link"
    },
    {
      client: "Video Reels & Edits",
      tags: "Across multiple clients",
      description: "Edited content from short-form social reels to long-form video. Built to perform on platform, not just to exist.",
      proofLink: "https://drive.google.com/drive/folders/193YgnA9EElY1D9hyGOiwIW05yjGQ5zUn?usp=drive_link"
    }
  ]
};

// Video section (A Quick Hello) — title, body, video, below-video text, 3 buttons only
export const videoSectionData = {
  title: "A QUICK HELLO",
  body: "I know it's a bit weird hiring someone you've never met. So here's a short video so you can get a feel for who you'd actually be working with — how I think, how I communicate, and whether we'd click.\n\nFair warning: I'm probably more normal than my metrics suggest.",
  videoUrl: "https://www.youtube.com/embed/p9IWM7fmNio",
  belowVideo: "If you've got something in mind, reach out however feels easiest.",
  buttons: [
    { label: "WhatsApp", url: "https://wa.link/immbpd", icon: "MessageCircle" },
    { label: "Email", url: "mailto:danzel.henson.1@gmail.com", icon: "Mail" },
    { label: "LinkedIn", url: "https://www.linkedin.com/in/danzelhenson/", icon: "Linkedin" }
  ]
};

// Final CTA — title, body, 5 buttons (contact + Watch intro + Download CV), reply note unchanged
export const contactData = {
  title: "Got something in mind?",
  body: "If any of this sounds like what you need, or even half of it, just reach out. Just tell me what's going on and we'll figure out if it makes sense.\n\nI don't bite. I do occasionally overthink things, but that usually works in your favor.",
  buttons: [
    { label: "WhatsApp", url: "https://wa.link/immbpd", icon: "MessageCircle" },
    { label: "Email", url: "mailto:danzel.henson.1@gmail.com", icon: "Mail" },
    { label: "LinkedIn", url: "https://www.linkedin.com/in/danzelhenson/", icon: "Linkedin" },
    { label: "Watch my intro", url: "https://youtu.be/p9IWM7fmNio", icon: "Play" },
    { label: "Download CV", url: "https://drive.google.com/file/d/1rBpVetQ1rhrtj83xDl0N895jmTYFFBNC/view?usp=sharing", icon: "Download" }
  ],
  closingLine: "I usually reply within 24 hours. If I don\u2019t, assume I\u2019m either asleep or temporarily defeated by life."
};

// Footer
export const footerData = {
  name: "Danzel Henson",
  tagline: "Marketing \u2022 Systems \u2022 Content \u2022 Operations",
  links: [
    { label: "LinkedIn", url: "https://www.linkedin.com/in/danzelhenson/" },
    { label: "Email", url: "mailto:danzel.henson.1@gmail.com" }
  ]
};
